import { OrderInformationBlock } from 'feature/appeals/components/OrderInformationBlock'
import { getAppealStepByClass } from 'feature/appeals/helpers/getAppealStepByClass'
import { TAppeal, TAppealType } from 'feature/appeals/type'
import { useEffect, useState } from 'react'

import { AppealHeader } from '../AppealHeader/AppealHeader'
import { AppealOffersList } from '../AppealOffersList/AppealOffersList'
import { DropDownBlock } from '../DropDownBlock/DropDownBlock'
import { OrderService } from '../OrderService/OrderService'
import { OrderVisit } from '../OrderVisit/OrderVisit'

import style from './OrderCard.module.scss'
import { FeedBackBlock } from '../FeedBackBlock/FeedBackBlock'

type TOrderCard = {
  data: TAppeal
}

type OrderCardConfig = {
  compositionTitle: string
  compositionTimeSlotText: string
  visitTitle: string
  visitEmptyDescription: string
  visitTimeSlotText: string
  visitZeroPriceText: string
  visitWriteFeedbackText: string
  visitYourFeedbackText: string
  offersTitle: string
  offersEmptyDescription: string
  hasSentBanner: boolean
  serviceTitle: string
  serviceEmptyDescription: string
  serviceTimeSlotText: string
  serviceWriteFeedbackText: string
  serviceYourFeedbackText: string
}

type OrderCardType = Exclude<TAppealType, 'CONSULTATION'>

const orderCardConfig: Record<OrderCardType, OrderCardConfig> = {
  ORDERING: {
    compositionTitle: 'состав заказа',
    compositionTimeSlotText: 'Дата и время замера',
    visitTitle: 'Замер',
    visitEmptyDescription: 'Здесь будут данные по проведению замера.',
    visitTimeSlotText: 'Согласованная дата и время замера',
    visitZeroPriceText: 'Скоро у Вас появится возможность оплатить замер',
    visitWriteFeedbackText: 'Оставьте отзыв о замере',
    visitYourFeedbackText: 'Ваша оценка замера',
    offersTitle: 'Заказ',
    offersEmptyDescription:
      'Здесь появится информация о стоимости заказа и оплате',
    hasSentBanner: true,
    serviceTitle: 'Монтаж',
    serviceEmptyDescription:
      'По готовности вашего заказа мы свяжемся с вами для согласования даты установки',
    serviceTimeSlotText: 'Дата и время монтажа',
    serviceWriteFeedbackText: 'Оставьте отзыв о монтаже',
    serviceYourFeedbackText: 'Ваша оценка монтажа',
  },
  SERVICE: {
    compositionTitle: 'состав заявки',
    compositionTimeSlotText: 'Дата и время диагностики',
    visitTitle: 'диагностика',
    visitEmptyDescription: 'Здесь будут данные по проведению диагностики.',
    visitTimeSlotText: 'Согласованная дата и время диагностики',
    visitZeroPriceText: 'Скоро у Вас появится возможность оплатить диагностику',
    visitWriteFeedbackText: 'Оставьте отзыв о диагностики',
    visitYourFeedbackText: 'Ваша оценка диагностики',
    offersTitle: 'Сервис',
    offersEmptyDescription:
      'После того, как мы подберем подходящие товары, они появятся здесь.',
    hasSentBanner: false,
    serviceTitle: 'ремонт',
    serviceEmptyDescription:
      'По готовности вашего заказа мы свяжемся с вами для согласования даты ремонта',
    serviceTimeSlotText: 'Дата и время ремонта',
    serviceWriteFeedbackText: 'Оставьте отзыв о ремонте',
    serviceYourFeedbackText: 'Ваша оценка ремонта',
  },
}

// раскрывающиеся блоки на детальной странице заявки (заказ и сервис)
export function OrderCard({ data }: TOrderCard) {
  const { visit, offers, service, status, orderIdList, type } = data
  const cfg = orderCardConfig[type as OrderCardType]

  const appealStep = getAppealStepByClass(status.status)
  const [activeBlockNumber, setActivBlockNumber] = useState(appealStep)

  useEffect(() => {
    setActivBlockNumber(appealStep)
  }, [status.status])

  const isVisitExist = visit !== null && visit.date !== null
  const descriptionArray = {
    visit: !isVisitExist ? cfg.visitEmptyDescription : null,
    offers: offers.length ? null : cfg.offersEmptyDescription,
    service: service ? null : cfg.serviceEmptyDescription,
  }

  // Разворачиваем только последний активный блок
  const isVisitVisibleInOfferStatus =
    status.status === 'OFFER' && offers.length === 0

  // Проверка, что сделка - лид и она в нужном статусе
  const isEdit =
    orderIdList.length === 0 &&
    (status.status === 'DEALER_SELECTED' || status.status === 'PREPROCESSING')

  return (
    <>
      <AppealHeader data={data} />
      <DropDownBlock
        title={cfg.compositionTitle}
        isOpen={activeBlockNumber === 0}
        testId='order-block-composition'
      >
        <OrderInformationBlock
          data={data}
          isEdit={isEdit}
          timeSlotText={cfg.compositionTimeSlotText}
        />
      </DropDownBlock>
      <DropDownBlock
        title={cfg.visitTitle}
        step='1'
        isDateExist={isVisitExist}
        description={descriptionArray.visit}
        isOpen={
          (activeBlockNumber === 1 && isVisitExist) ||
          isVisitVisibleInOfferStatus
        }
        testId='order-block-visit'
      >
        {isVisitExist && (
          <OrderVisit
            visit={visit}
            timeSlotText={cfg.visitTimeSlotText}
            zeroPriceText={cfg.visitZeroPriceText}
          >
            <FeedBackBlock
              grade={visit.grade}
              canGrade={visit.canGrade}
              writeFeedbackText={cfg.visitWriteFeedbackText}
              yourFeedbackText={cfg.visitYourFeedbackText}
              writeFeedbackBtnText='Оставить отзыв'
              yourFeedbackBtnText='Смотреть отзыв полностью'
              feedbackModalTitle='Отзыв о замере'
            />
          </OrderVisit>
        )}
      </DropDownBlock>
      <DropDownBlock
        title={cfg.offersTitle}
        step='2'
        isDateExist={!!offers.length}
        description={descriptionArray.offers}
        isOpen={activeBlockNumber === 2 && !isVisitVisibleInOfferStatus}
        testId='order-block-offers'
      >
        {cfg.hasSentBanner && offers[0]?.status === 'SENT' && (
          <>
            <span className={style.subtitle}>
              Вам необходимо выбрать одно из коммерческих предложений, нажав на
              кнопку &quot;Выбрать&quot;.
              <br />
            </span>
            <span className={style.subtitleBold}>
              Через личный кабинет вы заказываете оригинальные изделия от завода
              производителя DoorHan.
              <br />
              Услуги (монтаж, доставка и пр.), указанные в КП, оказываются нашим
              официальным партнером.
            </span>
          </>
        )}
        {!!offers.length && <AppealOffersList offers={offers} />}
      </DropDownBlock>
      <DropDownBlock
        title={cfg.serviceTitle}
        step='3'
        isDateExist={service !== null}
        description={descriptionArray.service}
        isOpen={activeBlockNumber === 3}
        testId='order-block-service'
      >
        {service && (
          <OrderService
            service={service}
            timeSlotText={cfg.serviceTimeSlotText}
          >
            <FeedBackBlock
              grade={service.grade}
              canGrade={service.canGrade}
              writeFeedbackText={cfg.serviceWriteFeedbackText}
              yourFeedbackText={cfg.serviceYourFeedbackText}
              writeFeedbackBtnText='Оставить отзыв'
              yourFeedbackBtnText='Смотреть отзыв полностью'
              feedbackModalTitle='Отзыв о монтаже'
            />
          </OrderService>
        )}
      </DropDownBlock>
    </>
  )
}
